import { useQueryAppStats } from "./useQueryAppStats";
import {
  PrivateAppWithImplementationsMeta,
  PublicAppWithImplementationsMeta,
} from "./useHydrateAppsWithImplementationsMeta";
import { AppStat } from "@zapier/bff";
interface Input {
  // The mixed-array member supports merged public + private lists (useApps
  // with filterType "all").
  apps: (
    | PublicAppWithImplementationsMeta
    | PrivateAppWithImplementationsMeta
  )[];
}

type PublicAppWithImplementationsMetaAndStats =
  PublicAppWithImplementationsMeta & {
    stats?: AppStat;
  };

type PrivateAppWithImplementationsMetaAndStats =
  PrivateAppWithImplementationsMeta & {
    stats?: AppStat;
  };

export type { PublicAppWithImplementationsMetaAndStats };
export type { PrivateAppWithImplementationsMetaAndStats };

/** This hook takes an array of apps and hydrates them with
 * a selected api stat from the `api/v4/accounts/:account_id/app-stats` endpoint.
 * This object is used for rendering the connection/auth count, member count and Zap count associated with an app.
 */

export function useHydrateAppsWithStats({ apps }: Input) {
  // Build a unique list of selectedApis referenced in the list
  const selectedApiIds = apps.reduce((accumulator, app) => {
    if (app.selectedApi) {
      accumulator.add(app.selectedApi);
    }
    return accumulator;
  }, new Set<string>());

  // Fetch app stats for the selectedApis
  // include member objects
  const { appStats, isLoading, isFetching } = useQueryAppStats(
    {
      selectedApis: [...selectedApiIds],
      includeMembers: true,
    },
    {
      enabled: !![...selectedApiIds]?.length,
    },
  );

  // Hydrate apps with app stat objects
  const hydratedApps = apps.map((app) => {
    const appStat = appStats?.find(
      (stat) => stat.selectedApi === app.selectedApi,
    );
    return {
      ...app,
      stats: {
        ...appStat,
      },
    };
  });

  return {
    apps: hydratedApps,
    isLoading,
    isFetching,
  };
}
